import React, { useCallback } from 'react';
import * as PropTypes from 'prop-types';
import ContactInfo, {
  ContactInfoNotesEdit,
  ContactInfoNotes,
  ContactInfoEdit,
  ContactInfoRepresentatives,
} from '@medflex/components/stories/ContactInfo';
import { constant } from '@medflex/commons';
import config from '@medflex/utils/config';
import { useHistory } from 'react-router';
import { PhoneNumberTags } from '@medflex/shared-types';
import getFullNameWithTitle from '../../../utils/getFullNameWithTitle';
import useGdt from '../../hooks/useGdt';

const ContactInfoBody = ({
  isContactInfoEditable,
  edit,
  user,
  mainContact,
  subContacts,
  country,
  isNotesEditable,
  state,
  handleSubmit,
  handleCancelEdit,
  autoFocus,
  handleEdit,
  hideDoB,
  hideHeader,
}) => {
  const history = useHistory();
  const handleOpenContactChat = useCallback(
    contactId => {
      history.push(`/address-book/${contactId}/overview`);
    },
    [history],
  );
  const { canOpenInPVS, handleOpenInPVS } = useGdt(user.pvsId);

  if (isContactInfoEditable && edit) {
    return (
      <div className="flex flex-col max-h-full bg-slate-100 rounded p-4">
        <ContactInfoEdit
          onSubmit={handleSubmit}
          state={state}
          initialValues={{
            firstName: user.firstName,
            lastName: user.lastName,
            dateOfBirth: user.dateOfBirth,
            note: user.note,
            pvsId: user.pvsId,
          }}
          disabled={!user}
          autofocus={autoFocus}
        />
      </div>
    );
  }

  return (
    <>
      <ContactInfo
        dateOfBirth={user.dateOfBirth}
        initials={`${user.firstName[0]}${user.lastName[0]}`}
        country={country}
        workArea={user.workArea}
        organization={user.organization}
        email={user.email}
        phoneNumbers={user.phoneNumbers}
        city={user.city}
        workplace={user.workplace}
        avatar={user.avatar}
        pvsId={user.pvsId}
        fullNameWithTitleAndSalutation={getFullNameWithTitle.get(user)}
        contactPageLink={user.slug ? `${config.PHYSICIAN_PAGES_URL}/${user.slug}` : null}
        areaOfActivity={user.areaOfActivity ? constant.areaOfActivity[user.areaOfActivity] : null}
        handleOpenInPVS={canOpenInPVS ? handleOpenInPVS : undefined}
        hideDoB={hideDoB}
        hideHeader={hideHeader}
      />
      {(mainContact || subContacts?.length > 0) && (
        <>
          <hr className="mb-4" />
          <ContactInfoRepresentatives
            mainContact={mainContact}
            subContacts={subContacts}
            onOpenContactChat={handleOpenContactChat}
          />
        </>
      )}
      {isNotesEditable && (
        <>
          <hr className="mb-4" />
          {edit ? (
            <ContactInfoNotesEdit
              state={state}
              onSubmit={handleSubmit}
              initialValues={{ note: user.note, pvsId: user.pvsId }}
              autofocus={autoFocus}
              handleCancelEdit={handleCancelEdit}
            />
          ) : (
            <ContactInfoNotes note={user.note} onEditNote={handleEdit} />
          )}
        </>
      )}
    </>
  );
};

ContactInfoBody.propTypes = {
  isContactInfoEditable: PropTypes.bool,
  edit: PropTypes.bool,
  dateOfBirth: PropTypes.string,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    dateOfBirth: PropTypes.string,
    organization: PropTypes.string,
    workArea: PropTypes.string,
    workplace: PropTypes.string,
    areaOfActivity: PropTypes.string,
    email: PropTypes.string,
    phoneNumbers: PropTypes.arrayOf(
      PropTypes.shape({
        tag: PropTypes.oneOf(Object.values(PhoneNumberTags)),
        number: PropTypes.string,
      }),
    ),
    city: PropTypes.string,
    countryCode: PropTypes.string,
    contactPageLink: PropTypes.string,
    initials: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    slug: PropTypes.string,
    note: PropTypes.string,
    pvsId: PropTypes.string,
  }),
  mainContact: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }),
  subContacts: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }),
  ),
  country: PropTypes.string,
  isNotesEditable: PropTypes.bool,
  state: PropTypes.oneOf(['composing', 'loading']),
  handleSubmit: PropTypes.func,
  autoFocus: PropTypes.bool,
  handleEdit: PropTypes.func,
  hideDoB: PropTypes.bool,
  hideHeader: PropTypes.bool,
  handleCancelEdit: PropTypes.func,
};

export default ContactInfoBody;
